import { ActionHandler, ActionTree, Commit } from 'vuex'

import { apolloClient } from '@/apollo-provider'
import { NotNull } from '@/utils'

import {
  AdoptOfferMutation,
  AdoptOfferMutationVariables,
  CancelOfferMutation,
  CancelOfferMutationVariables,
  CreateOfferMutation,
  CreateOfferMutationVariables,
  InterviewOfferPatch,
  InterviewOffersQuery,
  RescheduleOfferMutation,
  RescheduleOfferMutationVariables,
  SetExternalLinkMutation,
  SetExternalLinkMutationVariables,
  UpdateOfferMutation,
  UpdateOfferMutationVariables,
} from '@/graphql-schema'

import {
  AdoptOffer,
  AvailableInterviewDates,
  CancelOffer,
  CreateOffer,
  InterviewBookings,
  InterviewOffers,
  RescheduleOffer,
  SetExternalLink,
  UpdateOffer,
} from '@/store/interview/queries.graphql'

import { UncancelOfferMutation, UncancelOfferMutationVariables } from '@/graphql-schema'

import { UncancelOffer } from '@/store/interview/queries.graphql'
import { FetchResult } from 'apollo-link'

import { IsErrorHandlingAction } from '@/utils'
import { MutationOptions } from 'apollo-client'
import { DocumentNode } from 'graphql'
import { Mutation } from './mutations'
import { ModuleState, Offer, RootState } from './types'

interface OfferAction extends IsErrorHandlingAction {
  offer: Offer
}

export interface CancelAction extends OfferAction {
  type: 'cancel' | 'interview/offers/cancel'
}

export interface UncancelAction extends OfferAction {
  type: 'uncancel' | 'interview/offers/uncancel'
}

export interface AdoptAction extends OfferAction {
  type: 'adopt' | 'interview/offers/adopt'
}

export interface RescheduleAction extends OfferAction {
  type: 'reschedule' | 'interview/offers/reschedule'
}

export interface FetchAction {
  type: 'fetch' | 'offers/fetch' | 'interview/offers/fetch'
}

export interface WatchAction {
  type: 'watch' | 'interview/offers/watch'
}

export interface SetExternalLinkAction extends OfferAction {
  type: 'setExternalLink' | 'interview/offers/setExternalLink'
  link: string
}

export interface UpdateAction extends OfferAction {
  type: 'update' | 'interview/offers/update'
  oldId?: string
}

export interface CreateAction extends IsErrorHandlingAction {
  type: 'create' | 'interview/offers/create'
  startsAt: any
  endsAt: any
  interviewerId: string
}

export type Action =
  | AdoptAction
  | CancelAction
  | CreateAction
  | FetchAction
  | WatchAction
  | RescheduleAction
  | SetExternalLinkAction
  | UncancelAction
  | UpdateAction

export const actions: ActionTree<ModuleState, RootState> = {
  USER_LOGGED_OUT: {
    root: true,
    handler({ commit }) {
      commit<Mutation>({
        type: 'resetAll',
      })
    },
  },
  USER_SIGNED_IN: {
    root: true,
    handler({ dispatch }) {
      return dispatch<Action>({ type: 'watch' })
    },
  },
}

type FetchResultFor<M> = FetchResult<M, Record<string, any>, Record<string, any>>

export interface GraphqlActionParams<M, V extends object, A> {
  mutation: DocumentNode
  variables: (payload: A) => V
  resolved?: (value: FetchResultFor<M>) => any
  rejected?: (reason: any) => any
}

function graphqlAction<M, V extends object, A>({
  mutation,
  rejected,
  resolved,
  variables,
}: GraphqlActionParams<M, V, A>): ActionHandler<ModuleState, RootState> {
  const action: ActionHandler<ModuleState, RootState> = async ({ commit }, payload) => {
    const errorHandler = payload.onError

    const options: MutationOptions<M, V> = {
      errorPolicy: errorHandler ? 'all' : 'none',
      mutation,
      refetchQueries: [
        { query: InterviewOffers },
        { query: AvailableInterviewDates },
        { query: InterviewBookings },
      ],
      variables: variables(payload),
    }

    let promise = apolloClient.mutate<M, V>(options)

    if (errorHandler) {
      promise = promise.then(handleError(errorHandler, commit))
    }

    if (resolved) {
      promise = promise.then(resolved)
    }

    if (rejected) {
      promise = promise.catch(rejected)
    }

    return promise
  }

  return action
}

actions.cancel = async ({ dispatch, commit }, { offer, onError }: CancelAction) => {
  return apolloClient
    .mutate<CancelOfferMutation, CancelOfferMutationVariables>({
      errorPolicy: 'all',
      mutation: CancelOffer,
      refetchQueries: [
        { query: InterviewOffers },
        { query: AvailableInterviewDates },
        { query: InterviewBookings },
      ],
      variables: {
        id: offer.id,
      },
    })
    .then(handleError(onError, commit))
    .then(() => {
      dispatch(
        {
          messageName: `interviewer-canceled-offer-${offer.id}`,
          type: 'workflow/sendMessage',
        },
        { root: true }
      )
    })
}

actions.uncancel = graphqlAction<
  UncancelOfferMutation,
  UncancelOfferMutationVariables,
  UncancelAction
>({
  mutation: UncancelOffer,
  variables: ({ offer }) => ({ id: offer.id }),
})

actions.adopt = graphqlAction<AdoptOfferMutation, AdoptOfferMutationVariables, AdoptAction>({
  mutation: AdoptOffer,
  variables: ({ offer }) => ({ id: offer.id }),
})

actions.create = graphqlAction<CreateOfferMutation, CreateOfferMutationVariables, CreateAction>({
  mutation: CreateOffer,
  variables: ({ startsAt, endsAt, interviewerId }) => ({
    endsAt,
    interviewerId,
    startsAt,
  }),
})

actions.reschedule = async (
  { dispatch, commit },
  { offer: timeSlot, onError }: RescheduleAction
) => {
  console.log(timeSlot)
  return apolloClient
    .mutate<RescheduleOfferMutation, RescheduleOfferMutationVariables>({
      errorPolicy: 'all',
      mutation: RescheduleOffer,
      refetchQueries: [{ query: InterviewOffers }, { query: AvailableInterviewDates }],
      variables: {
        id: timeSlot.id,
        newDate: {
          timeSlot: {
            end: {
              inclusive: timeSlot.timeSlot!.end!.inclusive,
              value: timeSlot.timeSlot!.end!.value,
            },
            start: {
              inclusive: timeSlot.timeSlot!.start!.inclusive,
              value: timeSlot.timeSlot!.start!.value,
            },
          },
        },
      },
    })
    .then(handleError(onError, commit))
    .then(() => {
      dispatch(
        {
          messageName: `interviewer-rescheduled-offer-${timeSlot.id}`,
          type: 'workflow/sendMessage',
        },
        { root: true }
      )
    })
}

actions.watch = async ({ state, commit }) => {
  state.subscription = apolloClient
    .watchQuery<InterviewOffersQuery>({
      query: InterviewOffers,
    })
    .subscribe(
      result => {
        commit<Mutation>({
          offers: result.data!.offers,
          type: 'updateAll',
        })
      },
      error => {
        console.error(error)
        throw error
      },
      () => {
        console.log('ended watching')
      }
    )
}

actions.stopWatching = async ({ state }) => {
  if (state.subscription && !state.subscription.closed) {
    state.subscription.unsubscribe()
  }
}

actions.fetch = async ({ commit }) => {
  return apolloClient
    .query<InterviewOffersQuery>({
      fetchPolicy: 'no-cache',
      query: InterviewOffers,
    })
    .then(result => {
      commit<Mutation>({
        offers: result.data!.offers,
        type: 'updateAll',
      })
    })
}

actions.setExternalLink = async ({ commit }, { link, offer }: SetExternalLinkAction) => {
  return apolloClient.mutate<SetExternalLinkMutation, SetExternalLinkMutationVariables>({
    errorPolicy: 'all',
    mutation: SetExternalLink,
    refetchQueries: [{ query: InterviewOffers }],
    variables: {
      id: offer.id,
      link,
    },
  })
}

actions.update = async ({ dispatch, commit }, { offer, oldId }: UpdateAction) => {
  return apolloClient
    .mutate<UpdateOfferMutation, UpdateOfferMutationVariables>({
      errorPolicy: 'all',
      mutation: UpdateOffer,
      refetchQueries: [{ query: InterviewOffers }],
      variables: {
        id: oldId || offer.id,
        updates: offer,
      },
    })
    .then(async value => {
      await dispatch(
        { type: 'workflow/sendMessage', messageName: `interviewer-rescheduled-offer-${oldId}` },
        { root: true }
      )
      return value.data!.updateInterviewOfferById!.offer
    })
}

export default actions

function handleError(onError: ((error: any) => void) | undefined, commit: Commit) {
  return response => {
    if (!response.errors) {
      return response
    }
    response.errors.forEach(error => {
      if (!onError) {
        return commit<Mutation>({
          error,
          type: 'catchError',
        })
      }
      try {
        onError(error)
      } catch (exception) {
        commit<Mutation>({
          error: exception,
          type: 'catchError',
        })
      }
    })
    return response
  }
}
